import React, { useState, useEffect } from "react";
import useAuth from "../../Hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useForgotPasswordMutation,
  useLoginMutation,
  useVerify2faMutation,
  useGet2faSetupWithTokenMutation,
  useComplete2faSetupMutation,
  useRegisterDeviceReplacingMutation,
  useSendSecondaryCodeMutation,
  useVerifySecondaryCodeMutation,
} from "../../store";
import {
  useSendPasswordChangeEmailCodeMutation,
  useUpdateMyPasswordMutation,
} from "../../store/authApi";
import { useAlert } from "../../context/AlertContext";
import { getEmailSendErrorMessage } from "../../store/utils";
import { resolveLandingPath } from "../../utils/landing";
import LoginCard from "../../Components/Login/LoginCard";
import LoginForm from "../../Components/Login/LoginForm";
import ForgotPasswordForm from "../../Components/Login/ForgotPasswordForm";

const STEP_2FA_SECONDS = 5 * 60;        // 5 dk — 2FA kodu girişi
const STEP_2FA_SETUP_SECONDS = 10 * 60; // 10 dk — 2FA kurulum
const STEP_SECONDARY_SECONDS = 5 * 60;  // 5 dk — e-posta/SMS doğrulama
const STEP_DEVICE_CHOICE_SECONDS = 2 * 60; // 2 dk — cihaz seçimi

function formatDeviceDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSecondsLeft(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function isTokenExpired(token) {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return true;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
          .join("")
      )
    );
    return payload?.exp ? payload.exp * 1000 <= Date.now() : true;
  } catch {
    return true;
  }
}


export default function LoginPage() {
  const [forgotPassword, resultForgotPassword] = useForgotPasswordMutation();
  const [Login, resultLogin] = useLoginMutation();
  // 403 (yetki kaynakli giris engeli) mesaji — formun ustunde kalici gosterilir.
  const [loginBlockMessage, setLoginBlockMessage] = useState("");
  const [verify2fa, resultVerify2fa] = useVerify2faMutation();
  const [get2faSetupWithToken, resultSetupWithToken] = useGet2faSetupWithTokenMutation();
  const [complete2faSetup, resultCompleteSetup] = useComplete2faSetupMutation();
  const [registerDeviceReplacing, resultRegisterReplacing] = useRegisterDeviceReplacingMutation();
  const [sendSecondaryCode, resultSendSecondaryCode] = useSendSecondaryCodeMutation();
  const [verifySecondaryCode, resultVerifySecondaryCode] = useVerifySecondaryCodeMutation();
  const [sendPasswordChangeEmailCode, { isLoading: sendPasswordCodeLoading }] = useSendPasswordChangeEmailCodeMutation();
  const [updateMyPassword, resultUpdateMyPassword] = useUpdateMyPasswordMutation();
  const { auth, setAuth } = useAuth();
  const [pwd, setPwd] = useState("");
  const [email, setEmail] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [availableMethods, setAvailableMethods] = useState({ authenticator: false, email: true });
  const [secondaryCode, setSecondaryCode] = useState("");
  const [secondaryDeviceLabel, setSecondaryDeviceLabel] = useState("");
  const [choiceToken, setChoiceToken] = useState(null);
  const [devicesToReplace, setDevicesToReplace] = useState([]);
  const [newDeviceLabel, setNewDeviceLabel] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useAlert();
  const [step, setStep] = useState("login"); // "login" | "forgot" | "method_choice" | "2fa" | "2fa_setup" | "secondary_code" | "device_choice" | "must_change_password"
  const [mustChangePasswordInput, setMustChangePasswordInput] = useState({ passwordCurrent: "", password: "", passwordConfirm: "", emailCode: "", twoFactorCode: "" });
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [lastCodeSentInfo, setLastCodeSentInfo] = useState(null); // { email, operationId } — e-posta kodu hangi adrese ve hangi işlem no ile gitti
  const [timerEndsAt, setTimerEndsAt] = useState(null); // bu adımın bitiş zamanı (ms)
  const [tick, setTick] = useState(0); // her saniye güncellenir, kalan süre hesaplanır

  // Oturum sonlandığında (başka cihaz veya doğrulanmamış hesap) login sayfasında mesaj göster
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem("logoutReason");
      if (reason === "another_device") {
        sessionStorage.removeItem("logoutReason");
        showWarning("Başka bir cihazdan giriş yapıldı. Lütfen tekrar giriş yapınız.");
      } else if (reason === "account_not_verified") {
        sessionStorage.removeItem("logoutReason");
        showWarning("Doğrulanmamış hesap. Lütfen giriş yapıp iki adımlı doğrulama ve e-posta doğrulamasını tamamlayın.");
      } else if (reason === "session_expired") {
        sessionStorage.removeItem("logoutReason");
        showWarning("Oturumunuzun süresi doldu veya şifre değişti. Lütfen tekrar giriş yapınız.");
      } else if (reason === "trusted_device_revoked") {
        sessionStorage.removeItem("logoutReason");
        showWarning("Bu cihaz güvenilen listeden kaldırıldı. Lütfen tekrar giriş yapıp 2FA kodu girin.");
      }
    } catch (e) {}
  }, []);

  // Zaten giriş yapılmışsa (sayfayı tekrar açınca veya refresh ile token yenilendiyse) role göre anasayfaya yönlendir
  useEffect(() => {
    if (!auth?.accessToken || auth?.mustChangePassword === true || isTokenExpired(auth.accessToken)) return;
    const target = location.state?.from?.pathname?.startsWith("/Anasayfa")
      ? location.state.from.pathname
      : resolveLandingPath(auth);
    navigate(target, { replace: true, state: location.state?.from ? {} : undefined });
  }, [auth?.accessToken, auth?.role, auth?.subRole, auth?.mustChangePassword, location.state, navigate]);

  // Sayfa yüklendiğinde veya auth güncellendiğinde: ilk girişte şifre değiştir zorunluluğu varsa bu adımı göster
  useEffect(() => {
    if (auth?.mustChangePassword === true && step !== "must_change_password") {
      setStep("must_change_password");
      setEmailCodeSent(false);
      setMustChangePasswordInput({ passwordCurrent: "", password: "", passwordConfirm: "", emailCode: "", twoFactorCode: "" });
    }
  }, [auth?.mustChangePassword]);

  useEffect(() => {
    if (resultForgotPassword.isError === true) {
      // Sunucu mesajı: davet bekleyen hesap (#164), kayıtsız e-posta, limit
      showError(
        resultForgotPassword?.error?.data?.message || "İşlem başarısız tekrar deneyin !"
      );
    }
    if (resultForgotPassword.isSuccess === true) {
      showSuccess(
        resultForgotPassword?.data?.message ||
          "Şifre sıfırlama linki e-postanıza gönderildi. Link 2 dakika geçerlidir."
      );
    }
  }, [resultForgotPassword.isSuccess, resultForgotPassword.isError]);

  useEffect(() => {
    if (resultLogin.isError === true) {
      // Sunucu mesajini gosteriyoruz: yanlis sifrede kalan deneme hakkini,
      // 403'te ise NEDEN girilemedigini (uretim ekibi hesabi / hic yetki yok)
      // ancak sunucu bilebilir. Generic metin bu detaylari yutuyordu.
      const serverMessage = resultLogin?.error?.data?.message;
      showError(serverMessage || "İşlem başarısız tekrar deneyin !");
      // Yetki kaynakli engel kalici gosterilir — toast kaybolunca kullanici
      // yoneticisine ne soyleyecegini unutmasin.
      setLoginBlockMessage(
        resultLogin?.error?.status === 403 && serverMessage ? serverMessage : ""
      );
    }
    if (resultLogin.isSuccess === true) {
      const data = resultLogin?.data;
      if (data?.requires2FA === true && data?.tempToken) {
        setTempToken(data.tempToken);
        setAvailableMethods(data.availableMethods || { authenticator: false, email: true });
        setStep("method_choice");
        showWarning(data?.message || "Doğrulama yöntemini seçin.");
        setTimerEndsAt(Date.now() + STEP_2FA_SECONDS * 1000);
        return;
      }
      showSuccess("Giriş başarılı !");
      const accessToken = data?.token;
      const user = data?.data?.user;
      setAuth({ ...user, accessToken });
      navigate(resolveLandingPath(user));
    }
  }, [resultLogin.isSuccess, resultLogin.isError]);

  useEffect(() => {
    if (resultVerify2fa.isError === true) {
      showError(
        resultVerify2fa?.error?.data?.message ||
          "Doğrulama kodu hatalı veya süresi doldu. Tekrar giriş yapıp deneyin."
      );
    }
    if (resultVerify2fa.isSuccess === true) {
      const data = resultVerify2fa?.data;
      if (data?.requiresDeviceChoice === true && data?.choiceToken && data?.devices?.length) {
        setStep("device_choice");
        setChoiceToken(data.choiceToken);
        setDevicesToReplace(data.devices);
        setNewDeviceLabel(secondaryDeviceLabel?.trim() || "");
        showWarning(data?.message || "Bu cihazı kaydetmek için hangi cihazın yerine geçeceğini seçin.");
        setTimerEndsAt(Date.now() + STEP_DEVICE_CHOICE_SECONDS * 1000);
        return;
      }
      showSuccess("Giriş başarılı !");
      const accessToken = data?.token;
      const user = data?.data?.user;
      const mustChangePassword = data?.mustChangePassword === true;
      setAuth({ ...user, accessToken, mustChangePassword });
      if (mustChangePassword) {
        navigate("/Anasayfa/Profil");
      } else {
        navigate(resolveLandingPath(user));
      }
    }
  }, [resultVerify2fa.isSuccess, resultVerify2fa.isError]);

  const setAuthAndNavigate = (data) => {
    const accessToken = data?.token;
    const user = data?.data?.user;
    const mustChangePassword = data?.mustChangePassword === true;
    setAuth({ ...user, accessToken, mustChangePassword });
    if (mustChangePassword) {
      setStep("must_change_password");
      setEmailCodeSent(false);
      setMustChangePasswordInput({ passwordCurrent: "", password: "", passwordConfirm: "", emailCode: "", twoFactorCode: "" });
      return;
    }
    navigate(resolveLandingPath(user));
  };

  useEffect(() => {
    if (resultCompleteSetup.isError) {
      showError(resultCompleteSetup?.error?.data?.message || "Kod hatalı veya süresi doldu.");
    }
    if (resultCompleteSetup.isSuccess) {
      const data = resultCompleteSetup?.data;
      if (data?.requiresDeviceChoice === true && data?.choiceToken && data?.devices?.length) {
        setStep("device_choice");
        setChoiceToken(data.choiceToken);
        setDevicesToReplace(data.devices);
        setNewDeviceLabel("");
        showWarning(data?.message || "Bu cihazı kaydetmek için hangi cihazın yerine geçeceğini seçin.");
        setTimerEndsAt(Date.now() + STEP_DEVICE_CHOICE_SECONDS * 1000);
        return;
      }
      showSuccess("2FA kuruldu, giriş başarılı !");
      setAuthAndNavigate(resultCompleteSetup.data);
    }
  }, [resultCompleteSetup.isSuccess, resultCompleteSetup.isError]);

  useEffect(() => {
    if (resultRegisterReplacing.isError) {
      showError(resultRegisterReplacing?.error?.data?.message || "İşlem başarısız.");
    }
    if (resultRegisterReplacing.isSuccess) {
      showSuccess("Cihaz kaydedildi, giriş başarılı !");
      setAuthAndNavigate(resultRegisterReplacing.data);
    }
  }, [resultRegisterReplacing.isSuccess, resultRegisterReplacing.isError]);

  useEffect(() => {
    if (resultSendSecondaryCode.isError) {
      showError(getEmailSendErrorMessage(resultSendSecondaryCode?.error));
    }
    if (resultSendSecondaryCode.isSuccess) {
      const data = resultSendSecondaryCode?.data;
      setLastCodeSentInfo(
        data?.email != null || data?.operationId != null
          ? { email: data.email ?? "", operationId: data.operationId ?? "" }
          : null
      );
      setStep("secondary_code");
      showSuccess("Doğrulama kodu e-postanıza gönderildi.");
      setTimerEndsAt(Date.now() + STEP_SECONDARY_SECONDS * 1000);
    }
  }, [resultSendSecondaryCode.isSuccess, resultSendSecondaryCode.isError]);

  useEffect(() => {
    if (resultVerifySecondaryCode.isError) {
      showError(resultVerifySecondaryCode?.error?.data?.message || "Kod hatalı veya süresi doldu.");
    }
    if (resultVerifySecondaryCode.isSuccess) {
      const data = resultVerifySecondaryCode?.data;
      if (data?.requiresDeviceChoice === true && data?.choiceToken && data?.devices?.length) {
        setStep("device_choice");
        setChoiceToken(data.choiceToken);
        setDevicesToReplace(data.devices);
        setNewDeviceLabel(secondaryDeviceLabel?.trim() || "");
        showWarning(data?.message || "Bu cihazı kaydetmek için hangi cihazın yerine geçeceğini seçin.");
        setTimerEndsAt(Date.now() + STEP_DEVICE_CHOICE_SECONDS * 1000);
        return;
      }
      showSuccess("Giriş başarılı !");
      setAuthAndNavigate(data);
    }
  }, [resultVerifySecondaryCode.isSuccess, resultVerifySecondaryCode.isError]);

  // İlk giriş şifre değişikliği (login akışında) başarılı → panele yönlendir
  useEffect(() => {
    if (resultUpdateMyPassword.isError && step === "must_change_password") {
      showError(resultUpdateMyPassword?.error?.data?.message || "Şifre güncellenemedi.");
    }
    if (resultUpdateMyPassword.isSuccess && step === "must_change_password") {
      showSuccess("Şifre güncellendi. Yönlendiriliyorsunuz...");
      const data = resultUpdateMyPassword.data;
      const user = data?.data?.user;
      const accessToken = data?.token;
      setAuth({ ...user, accessToken, mustChangePassword: false });
      navigate(resolveLandingPath(user));
    }
  }, [resultUpdateMyPassword.isSuccess, resultUpdateMyPassword.isError, step]);

  // Zamanlayıcı: 2fa / 2fa_setup / secondary_code / device_choice adımlarında saniye bazlı geri sayım
  const isTimedStep = step === "method_choice" || step === "2fa" || step === "2fa_setup" || step === "secondary_code" || step === "device_choice";
  useEffect(() => {
    if (!isTimedStep || !timerEndsAt) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [isTimedStep, timerEndsAt]);

  const secondsLeft = timerEndsAt && isTimedStep
    ? Math.max(0, Math.ceil((timerEndsAt - Date.now()) / 1000))
    : 0;
  const stepExpired = isTimedStep && secondsLeft === 0;

  const handleForgetPassword = (e) => {
    e.preventDefault();
    forgotPassword(email);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginBlockMessage(""); // yeni deneme — onceki engel mesaji temizlenir
    Login({ password: pwd, email });
  };
  const handle2FASubmit = (e) => {
    e.preventDefault();
    if (!tempToken || twoFACode.trim().length !== 6) return;
    verify2fa({ tempToken, code: twoFACode.trim() });
  };
  const handle2FABack = () => {
    setStep("login");
    setTempToken(null);
    setSecondaryCode("");
    setChoiceToken(null);
    setDevicesToReplace([]);
    setTwoFACode("");
    setTimerEndsAt(null);
  };
  // method_choice: Authenticator seçildi
  const handleChooseAuthenticator = () => {
    if (availableMethods?.authenticator) {
      setStep("2fa");
      setTimerEndsAt(Date.now() + STEP_2FA_SECONDS * 1000);
    } else {
      // Kurulu değil → login içinde QR ile kur
      setStep("2fa_setup");
      setTimerEndsAt(Date.now() + STEP_2FA_SETUP_SECONDS * 1000);
      get2faSetupWithToken(tempToken);
    }
  };
  // method_choice: E-posta seçildi
  const handleChooseEmail = () => {
    if (!tempToken) return;
    sendSecondaryCode({ tempToken, method: "email" });
  };
  const handleSecondarySubmit = (e) => {
    e.preventDefault();
    if (!tempToken || secondaryCode.trim().length !== 6) return;
    verifySecondaryCode({
      tempToken,
      code: secondaryCode.trim(),
      deviceLabel: secondaryDeviceLabel?.trim() || undefined,
    });
  };
  const handle2FASetupSubmit = (e) => {
    e.preventDefault();
    if (!tempToken || twoFACode.trim().length !== 6) return;
    complete2faSetup({ tempToken, code: twoFACode.trim() });
  };
  const handleDeviceReplace = (replaceDeviceId) => {
    if (!choiceToken) return;
    registerDeviceReplacing({
      choiceToken,
      replaceDeviceId,
      deviceLabel: newDeviceLabel?.trim() || undefined,
    });
  };
  const handleDeviceChoiceSkip = () => {
    setStep("login");
    setChoiceToken(null);
    setDevicesToReplace([]);
    setTempToken(null);
    setTwoFACode("");
    setTimerEndsAt(null);
    showWarning("Cihaz kaydedilmeden çıkış yaptınız. Tekrar giriş yapıp 2FA kodu ile giriş yapabilirsiniz.");
  };

  const handleSendMustChangeEmailCode = () => {
    if (!auth?.accessToken) return;
    sendPasswordChangeEmailCode(auth.accessToken).then(() => setEmailCodeSent(true)).catch(() => {});
  };

  const handleMustChangePasswordSubmit = (e) => {
    e.preventDefault();
    if (!auth?.accessToken) return;
    const { passwordCurrent, password, passwordConfirm, emailCode, twoFactorCode } = mustChangePasswordInput;
    if (!passwordCurrent?.trim() || !password?.trim() || password !== passwordConfirm) return;
    const twoFactorEnabled = auth?.twoFactorEnabled === true;
    if (twoFactorEnabled && (twoFactorCode || "").trim().length !== 6) return;
    if (!twoFactorEnabled && (!emailCodeSent || (emailCode || "").trim().length !== 6)) return;
    const body = {
      token: auth.accessToken,
      passwordCurrent: passwordCurrent.trim(),
      password: password.trim(),
      passwordConfirm: passwordConfirm.trim(),
    };
    if (twoFactorEnabled) body.twoFactorCode = (twoFactorCode || "").trim();
    else body.emailCode = (emailCode || "").trim();
    updateMyPassword(body);
  };

  const handleMustChangePasswordBack = () => {
    setAuth(null);
    setStep("login");
    setEmailCodeSent(false);
    setMustChangePasswordInput({ passwordCurrent: "", password: "", passwordConfirm: "", emailCode: "", twoFactorCode: "" });
  };

  return (
    <div className="min-h-screen flex relative z-0">
      {/* Sol: Form alanı — arka plan görünsün; sadece kutu opak */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 relative z-10">
        <div className="w-full max-w-md">
          <div className="bg-[#ffffff] rounded-xl border-2 border-slate-200 shadow-xl p-6 sm:p-8 min-h-[320px]">
            <LoginCard>
          {step === "method_choice" ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-main">Doğrulama yöntemi seçin</h2>
                {!stepExpired && (
                  <p className="text-sm font-mono font-medium text-amber-600 shrink-0 ml-2" title="Kalan süre">
                    {formatSecondsLeft(secondsLeft)}
                  </p>
                )}
              </div>
              {stepExpired ? (
                <>
                  <p className="text-fifth font-medium">Süre doldu. Tekrar giriş yapıp doğrulama yöntemini seçin.</p>
                  <button
                    type="button"
                    onClick={handle2FABack}
                    className="w-full rounded-lg bg-main px-4 py-3 text-new hover:opacity-90"
                  >
                    Girişe dön
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-main/70">
                    Girişi tamamlamak için bir yöntem seçin.
                  </p>
                  <button
                    type="button"
                    onClick={handleChooseAuthenticator}
                    className="w-full rounded-lg bg-main px-4 py-3 text-new hover:opacity-90"
                  >
                    {availableMethods?.authenticator
                      ? "Authenticator uygulaması ile"
                      : "Authenticator kur ve gir"}
                  </button>
                  <button
                    type="button"
                    onClick={handleChooseEmail}
                    disabled={resultSendSecondaryCode.isLoading}
                    className="w-full rounded-lg border border-main px-4 py-3 text-main hover:bg-input disabled:opacity-50"
                  >
                    {resultSendSecondaryCode.isLoading ? "Gönderiliyor..." : "E-posta kodu ile"}
                  </button>
                  <button
                    type="button"
                    onClick={handle2FABack}
                    className="w-full py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    Girişe dön
                  </button>
                </>
              )}
            </div>
          ) : step === "2fa_setup" ? (
            <form onSubmit={handle2FASetupSubmit} className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-700 font-medium">İki adımlı doğrulama (2FA) zorunludur</p>
                {!stepExpired && (
                  <p className="text-sm font-mono font-medium text-amber-600" title="Kalan süre">
                    {formatSecondsLeft(secondsLeft)}
                  </p>
                )}
              </div>
              {stepExpired ? (
                <>
                  <p className="text-red-600 font-medium">Süre doldu. Tekrar giriş yapıp 2FA kurulumunu başlatın.</p>
                  <button
                    type="button"
                    onClick={handle2FABack}
                    className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Girişe dön
                  </button>
                </>
              ) : (
                <>
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-left">
                    <p className="text-amber-800 font-medium text-sm mb-2">Google Authenticator gerekli</p>
                    <p className="text-amber-700 text-xs mb-2">
                      İki adımlı doğrulama için telefonunuza <strong>Google Authenticator</strong> (veya benzeri bir TOTP uygulaması) indirmeniz gerekiyor. Aşağıdaki adımları izleyin:
                    </p>
                    <ol className="text-amber-700 text-xs list-decimal list-inside space-y-1">
                      <li>App Store veya Play Store üzerinden &quot;Google Authenticator&quot; uygulamasını indirin.</li>
                      <li>Uygulamayı açın, &quot;Hesap ekle&quot; → &quot;QR kod tara&quot; ile aşağıdaki kare kodu tarayın; veya &quot;Manuel giriş&quot; ile altta görünen kodu yapıştırın.</li>
                      <li>Uygulamanın ürettiği 6 haneli kodu aşağıdaki kutuya yazıp &quot;Kurulumu tamamla&quot;ya tıklayın.</li>
                    </ol>
                    <p className="text-amber-600 text-xs mt-2">
                      <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer" className="underline">App Store</a>
                      {" · "}
                      <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer" className="underline">Play Store</a>
                    </p>
                    <p className="text-amber-700 text-xs mt-2 pt-2 border-t border-amber-200">
                      Bu doğrulama sadece yeni bir cihazdan giriş yapıldığında ilk defa sorulur; aynı cihazdan sonraki girişlerinizde doğrulama sorulmaz.
                    </p>
                  </div>
                  <p className="text-gray-600 text-sm">
                    QR kodu veya aşağıdaki gizli kodu uygulamaya ekledikten sonra, uygulamanın verdiği 6 haneli kodu aşağıya yazın.
                  </p>
                  {resultSetupWithToken.isLoading && (
                    <p className="text-sm text-gray-500">QR kodu hazırlanıyor...</p>
                  )}
                  {resultSetupWithToken.isError && (
                    <p className="text-sm text-red-600">
                      {resultSetupWithToken?.error?.data?.message || "QR alınamadı. Tekrar giriş yapıp deneyin."}
                    </p>
                  )}
                  {resultSetupWithToken?.data?.data?.qrCodeUrl && (
                    <div className="flex justify-center py-2">
                      <img
                        src={resultSetupWithToken.data.data.qrCodeUrl}
                        alt="2FA QR"
                        className="w-40 h-40 object-contain border border-gray-200 rounded-lg"
                      />
                    </div>
                  )}
                  {resultSetupWithToken?.data?.data?.secretBase32 && (
                    <p className="text-xs text-gray-500 break-all font-mono bg-gray-100 p-2 rounded">
                      {resultSetupWithToken.data.data.secretBase32}
                    </p>
                  )}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={twoFACode}
                    onChange={(e) =>
                      setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-center text-lg tracking-widest"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handle2FABack}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Geri
                    </button>
                    <button
                      type="submit"
                      disabled={
                        twoFACode.trim().length !== 6 || resultCompleteSetup.isLoading
                      }
                      className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {resultCompleteSetup.isLoading ? "Kuruluyor..." : "Kurulumu tamamla"}
                    </button>
                  </div>
                </>
              )}
            </form>
          ) : step === "secondary_code" ? (
            <form onSubmit={handleSecondarySubmit} className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-700 font-medium">E-postanıza gelen 6 haneli kodu girin</p>
                {!stepExpired && (
                  <p className="text-sm font-mono font-medium text-amber-600 shrink-0 ml-2" title="Kalan süre">
                    {formatSecondsLeft(secondsLeft)}
                  </p>
                )}
              </div>
              <p className="text-gray-500 text-xs">
                Bu doğrulama sadece yeni bir cihazdan giriş yapıldığında ilk defa sorulur; aynı cihazdan sonraki girişlerinizde doğrulama sorulmaz.
              </p>
              {lastCodeSentInfo && (lastCodeSentInfo.email || lastCodeSentInfo.operationId) && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-left text-sm text-slate-700">
                  {lastCodeSentInfo.email && (
                    <p>Kod <strong>{lastCodeSentInfo.email}</strong> adresine gönderildi.</p>
                  )}
                  {lastCodeSentInfo.operationId && (
                    <p className="mt-0.5">İşlem no: <strong className="font-mono">{lastCodeSentInfo.operationId}</strong> (e-postadaki kodla eşleşir)</p>
                  )}
                </div>
              )}
              {stepExpired ? (
                <>
                  <p className="text-red-600 font-medium">Süre doldu. Girişe dönüp tekrar 2FA ve doğrulama adımlarını tamamlayın.</p>
                  <button
                    type="button"
                    onClick={handle2FABack}
                    className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Girişe dön
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={secondaryCode}
                    onChange={(e) =>
                      setSecondaryCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-center text-lg tracking-widest"
                    autoFocus
                  />
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Cihaz adı (isteğe bağlı)</label>
                    <input
                      type="text"
                      maxLength={100}
                      placeholder="Örn. İş bilgisayarı"
                      value={secondaryDeviceLabel}
                      onChange={(e) => setSecondaryDeviceLabel(e.target.value.slice(0, 100))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handle2FABack}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Geri
                    </button>
                    <button
                      type="submit"
                      disabled={secondaryCode.trim().length !== 6 || resultVerifySecondaryCode.isLoading}
                      className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resultVerifySecondaryCode.isLoading ? "Doğrulanıyor..." : "Doğrula"}
                    </button>
                  </div>
                </>
              )}
            </form>
          ) : step === "device_choice" ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-700 font-medium">Kayıtlı {devicesToReplace?.length ?? 0} cihazınız var. Bu yeni cihazı kaydetmek için hangi cihazın yerine geçeceğini seçin.</p>
                {!stepExpired && (
                  <p className="text-sm font-mono font-medium text-amber-600" title="Kalan süre">
                    {formatSecondsLeft(secondsLeft)}
                  </p>
                )}
              </div>
              {stepExpired ? (
                <>
                  <p className="text-red-600 font-medium">Süre doldu. Tekrar giriş yapıp 2FA kodu ile cihaz seçimine ulaşın.</p>
                  <button
                    type="button"
                    onClick={handle2FABack}
                    className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Girişe dön
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-600 text-sm">
                    Aşağıda hesabınıza kayıtlı cihazlar görünüyor. Bu cihazı kaydetmek için birinin yerine geçmesini seçin.
                  </p>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Bu cihaz için isim (isteğe bağlı)</label>
                    <input
                      type="text"
                      maxLength={100}
                      placeholder="Örn. Ev laptop"
                      value={newDeviceLabel}
                      onChange={(e) => setNewDeviceLabel(e.target.value.slice(0, 100))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                  </div>
                  <ul className="space-y-2">
                    {devicesToReplace.map((dev) => (
                      <li
                        key={dev._id}
                        className="flex flex-wrap items-center justify-between gap-2 p-3 border border-gray-200 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-800">{dev.deviceName || "Bilinmeyen cihaz"}</p>
                          <p className="text-xs text-gray-500">
                            {dev.deviceType === "mobile" ? "Mobil" : "Web"} • Son: {formatDeviceDate(dev.lastUsedAt)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeviceReplace(dev._id)}
                          disabled={resultRegisterReplacing.isLoading}
                          className="py-1.5 px-3 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                          Yerine kaydet
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={handleDeviceChoiceSkip}
                    className="w-full py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    Geri (giriş yapmadan çık)
                  </button>
                </>
              )}
            </div>
          ) : step === "2fa" ? (
            <form onSubmit={handle2FASubmit} className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-600 text-sm mb-2">
                  Hesabınızda iki adımlı doğrulama açık. Google Authenticator uygulamasındaki 6 haneli kodu girin.
                </p>
                {!stepExpired && (
                  <p className="text-sm font-mono font-medium text-amber-600 shrink-0 ml-2" title="Kalan süre">
                    {formatSecondsLeft(secondsLeft)}
                  </p>
                )}
              </div>
              <p className="text-gray-500 text-xs mb-2">
                Bu doğrulama sadece yeni bir cihazdan giriş yapıldığında ilk defa sorulur; aynı cihazdan sonraki girişlerinizde doğrulama sorulmaz.
              </p>
              {stepExpired ? (
                <>
                  <p className="text-red-600 font-medium">Süre doldu. Tekrar giriş yapıp 2FA kodunu girin.</p>
                  <button
                    type="button"
                    onClick={handle2FABack}
                    className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Girişe dön
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={twoFACode}
                    onChange={(e) =>
                      setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-center text-lg tracking-widest"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handle2FABack}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Geri
                    </button>
                    <button
                      type="submit"
                      disabled={
                        twoFACode.trim().length !== 6 || resultVerify2fa.isLoading
                      }
                      className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resultVerify2fa.isLoading ? "Doğrulanıyor..." : "Doğrula"}
                    </button>
                  </div>
                </>
              )}
            </form>
          ) : step === "must_change_password" ? (
            <div className="space-y-4">
              <p className="text-gray-700 font-medium">İlk girişte şifre değiştirmeniz gerekiyor</p>
              <p className="text-gray-600 text-sm">
                Hesabınızı güvenli hale getirmek için yeni bir şifre belirleyin.
              </p>
              {resultUpdateMyPassword.isError && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  {resultUpdateMyPassword?.error?.data?.message || "Şifre güncellenemedi."}
                </p>
              )}
              {auth?.twoFactorEnabled ? (
                <form onSubmit={handleMustChangePasswordSubmit} className="space-y-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="2FA kodu (6 hane)"
                    value={mustChangePasswordInput.twoFactorCode}
                    onChange={(e) => setMustChangePasswordInput((p) => ({ ...p, twoFactorCode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-center tracking-widest"
                  />
                  <input
                    type="password"
                    placeholder="Mevcut şifre"
                    value={mustChangePasswordInput.passwordCurrent}
                    onChange={(e) => setMustChangePasswordInput((p) => ({ ...p, passwordCurrent: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="password"
                    placeholder="Yeni şifre (en az 6 karakter)"
                    value={mustChangePasswordInput.password}
                    onChange={(e) => setMustChangePasswordInput((p) => ({ ...p, password: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="password"
                    placeholder="Yeni şifre tekrar"
                    value={mustChangePasswordInput.passwordConfirm}
                    onChange={(e) => setMustChangePasswordInput((p) => ({ ...p, passwordConfirm: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <div className="flex gap-2 pt-2">
                    <button type="button" onClick={handleMustChangePasswordBack} className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
                      Geri (çıkış)
                    </button>
                    <button
                      type="submit"
                      disabled={
                        resultUpdateMyPassword.isLoading ||
                        (mustChangePasswordInput.twoFactorCode || "").trim().length !== 6 ||
                        !mustChangePasswordInput.passwordCurrent?.trim() ||
                        !mustChangePasswordInput.password?.trim() ||
                        mustChangePasswordInput.password !== mustChangePasswordInput.passwordConfirm
                      }
                      className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50"
                    >
                      {resultUpdateMyPassword.isLoading ? "Güncelleniyor..." : "Şifreyi güncelle"}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {!emailCodeSent ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">E-posta adresinize doğrulama kodu göndereceğiz. Ardından yeni şifrenizi girin.</p>
                      <button
                        type="button"
                        onClick={handleSendMustChangeEmailCode}
                        disabled={sendPasswordCodeLoading}
                        className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50"
                      >
                        {sendPasswordCodeLoading ? "Gönderiliyor..." : "E-postaya kod gönder"}
                      </button>
                      <button type="button" onClick={handleMustChangePasswordBack} className="w-full py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
                        Geri (çıkış)
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleMustChangePasswordSubmit} className="space-y-3">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="E-posta kodu (6 hane)"
                        value={mustChangePasswordInput.emailCode}
                        onChange={(e) => setMustChangePasswordInput((p) => ({ ...p, emailCode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-center tracking-widest"
                      />
                      <input
                        type="password"
                        placeholder="Mevcut şifre"
                        value={mustChangePasswordInput.passwordCurrent}
                        onChange={(e) => setMustChangePasswordInput((p) => ({ ...p, passwordCurrent: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        type="password"
                        placeholder="Yeni şifre (en az 6 karakter)"
                        value={mustChangePasswordInput.password}
                        onChange={(e) => setMustChangePasswordInput((p) => ({ ...p, password: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        type="password"
                        placeholder="Yeni şifre tekrar"
                        value={mustChangePasswordInput.passwordConfirm}
                        onChange={(e) => setMustChangePasswordInput((p) => ({ ...p, passwordConfirm: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                      <div className="flex gap-2 pt-2">
                        <button type="button" onClick={() => setEmailCodeSent(false)} className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">
                          Geri
                        </button>
                        <button
                          type="submit"
                          disabled={
                            resultUpdateMyPassword.isLoading ||
                            (mustChangePasswordInput.emailCode || "").trim().length !== 6 ||
                            !mustChangePasswordInput.passwordCurrent?.trim() ||
                            !mustChangePasswordInput.password?.trim() ||
                            mustChangePasswordInput.password !== mustChangePasswordInput.passwordConfirm
                          }
                          className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50"
                        >
                          {resultUpdateMyPassword.isLoading ? "Güncelleniyor..." : "Şifreyi güncelle"}
                        </button>
                      </div>
                      <button type="button" onClick={handleMustChangePasswordBack} className="w-full py-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 text-sm">
                        Çıkış yap
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>
          ) : step === "forgot" ? (
            <ForgotPasswordForm
              email={email}
              setEmail={setEmail}
              onSubmit={handleForgetPassword}
              onBack={() => {
                setStep("login");
                setEmail("");
              }}
              isLoading={resultForgotPassword.isLoading}
            />
          ) : (
            <LoginForm
              email={email}
              setEmail={setEmail}
              pwd={pwd}
              setPwd={setPwd}
              onSubmit={handleSubmit}
              onForgot={() => {
                setStep("forgot");
                setEmail("");
                setPwd("");
              }}
              isLoading={resultLogin.isLoading}
              blockMessage={loginBlockMessage}
            />
          )}
            </LoginCard>
          </div>
        </div>
      </div>

      {/* Sağ: Marka paneli — sidebar tarzı */}
      <div className="hidden lg:flex w-[420px] min-h-screen flex-col items-center justify-center bg-slate-800 px-10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${require("../../Assets/img/background.jpg")})` }}
        />
        <div className="relative z-10 text-center">
          <h2 className="text-2xl font-bold text-white tracking-tight">BAYINER</h2>
          <p className="text-slate-300 text-sm mt-3 max-w-xs">
            Cihaz ve hesap yönetimine güvenli giriş yapın.
          </p>
          <div className="mt-10 flex justify-center gap-4 text-slate-400">
            <span className="text-xs">Güvenli giriş</span>
            <span className="text-xs">2FA destekli</span>
          </div>
        </div>
      </div>
    </div>
  );
}
